import { isNil } from 'lodash'

export const M = 1000 * 1000

type BaseFormatOptions = { omitUnit?: boolean }
type PrecisionOptions = { increasePrecision?: boolean }
export type CommonFormatOptions = BaseFormatOptions
export type FullFormatOptions = BaseFormatOptions &
  PrecisionOptions & {
    forExport?: boolean
  }

type PriceFormatOptions = BaseFormatOptions & { decreasePrecision?: boolean }

/**
 * By default, format the input value while rounding to an integer.
 *
 * For example:
 * - 123.456 => "123"
 * - 1.23456 => "1"
 * - 0.123456 => "0"
 *
 * Optionally, a unit can be added.
 *
 * For example:
 * - 123.456, kW => "123 kW"
 *
 * Optionally, the precision can be increased for small numbers.
 *
 * For example:
 * - 123.456 => "123"
 * - 1.23456 => "1.2"
 * - 0.123456 => "0.1"
 *
 * @param value The value to format
 * @param unit The unit of the value
 * @param increasePrecision Increase the precision for small numbers
 */
const formatNumber = (
  value: number | undefined | null,
  unit?: string,
  increasePrecision?: boolean,
) => {
  if (isNil(value)) {
    return ''
  }

  const suffix = `${unit ? ` ${unit}` : ''}`

  if (Number.isNaN(value)) {
    return `-${suffix}`
  }
  if (value === Infinity) {
    return `∞${suffix}`
  }

  if (increasePrecision && value >= 0.05 && value < 9.95) {
    return `${value.toLocaleString('fi-FI', {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    })}${suffix}`
  }

  return `${value.toLocaleString('fi-FI', {
    maximumFractionDigits: 0,
  })}${suffix}`
}

const formatNumberForExport = (value: number | undefined | null) =>
  isNil(value)
    ? ''
    : value.toLocaleString('en-US', { maximumSignificantDigits: 4 })

export const areaUnit = 'm²'
export const lengthUnit = 'm'
export const temperatureUnit = '°C'
export const temperatureKelvinUnit = 'K'
export const hourUnit = 'h'
export const monthUnit = 'kk'
export const energyCapacityUnit = 'kWh'
export const energyCapacityPerAreaUnit = 'kWh/m²'
export const energyPerYearUnit = 'MWh/a'
export const energyPerAreaUnit = 'kWh/(m²⋅a)'
export const energyPerLengthUnit = 'kWh/(m⋅a)'
export const powerUnit = 'kW'
export const powerPer1000Unit = 'W'
export const powerPerAreaUnit = 'W/m²'
export const powerPerLengthUnit = 'W/m'
export const energyPriceUnit = '€/MWh'
export const emissionUnit = 'kg CO₂e'
export const emissionPerYearUnit = 'kg CO₂e/a'
export const largeEmissionUnit = 't CO₂e'
export const largeEmissionPerYearUnit = 't CO₂e/a'
export const emissionPerAreaUnit = 'kg CO₂e/(m²⋅a)'
export const emissionPerEnergyUnit = 'kg CO₂e/MWh'
export const priceUnit = '€'
export const pricePerYearUnit = '€/a'

export const airFlowUnit = 'm³/s'
export const totalSpecificAirFlowPerAreaUnit = 'l/s/m²'
export const uValueUnit = 'W/m²K'
export const uaValueUnit = 'W/K'

export const formatArea = (area: number): string => formatNumber(area, areaUnit)

/**
 * Format energy in MWh/a.
 * @param energy The energy in Wh
 * @param options.omitUnit
 * @param options.increasePrecision Add more precision to small values
 * @returns 12 MWh/a | 0,2 | ...
 */
export const formatEnergy = (
  energy: number,
  options?: FullFormatOptions,
): string =>
  options?.forExport
    ? formatNumberForExport(energy / (1000 * 1000))
    : formatNumber(
        energy / (1000 * 1000),
        options?.omitUnit ? undefined : energyPerYearUnit,
        options?.increasePrecision,
      )

/**
 * Format energy capacity in kWh.
 * @param energy The energy in Wh
 * @param options.omitUnit
 * @param options.increasePrecision Add more precision to small values
 * @returns 12 kWh | 0,2 | ...
 */
export const formatEnergyCapacity = (
  energy: number,
  options?: FullFormatOptions,
): string =>
  options?.forExport
    ? formatNumberForExport(energy / 1000)
    : formatNumber(
        energy / 1000,
        options?.omitUnit ? undefined : energyCapacityUnit,
        options?.increasePrecision,
      )

export const formatEnergyPerArea = (
  energy: number,
  area: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    energy / (area * 1000),
    options?.omitUnit ? undefined : energyPerAreaUnit,
  )

export const formatEnergyPerLength = (
  energy: number,
  length: number,
  options?: CommonFormatOptions,
): string =>
  length > 0
    ? formatNumber(
        energy / (length * 1000),
        options?.omitUnit ? undefined : energyPerLengthUnit,
      )
    : '-'

export const formatPower = (
  power: number | undefined | null,
  options?: FullFormatOptions,
): string =>
  options?.forExport
    ? formatNumberForExport(!isNil(power) ? power / 1000 : undefined)
    : formatNumber(
        !isNil(power) ? power / 1000 : undefined,
        options?.omitUnit ? undefined : powerUnit,
        options?.increasePrecision,
      )

export const formatPowerPerArea = (
  power: number,
  area: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(power / area, options?.omitUnit ? undefined : powerPerAreaUnit)

export const formatPowerPerLength = (
  power: number,
  length: number,
  options?: CommonFormatOptions,
): string =>
  length > 0
    ? formatNumber(
        power / length,
        options?.omitUnit ? undefined : powerPerLengthUnit,
      )
    : '-'

export const formatPercent = (
  percent: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(Math.round(percent * 100), options?.omitUnit ? undefined : '%')

export const formatPercentWithPrecision = (
  percent: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(percent * 100, options?.omitUnit ? undefined : '%', true)

export const formatCoefficient = (coefficient: number): string =>
  formatNumber(coefficient, undefined, true)

export const formatTemperature = (
  temperature: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(temperature, options?.omitUnit ? undefined : temperatureUnit)

export const formatHour = (hour: number): string => formatNumber(hour, hourUnit)

export const formatLength = (length: number): string =>
  formatNumber(length, lengthUnit)

export const formatEnergyPrice = (
  energyPrice: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    energyPrice,
    options?.omitUnit ? undefined : energyPriceUnit,
    true,
  )

export const formatPrice = (
  price: number,
  options?: PriceFormatOptions,
): string => {
  if (options?.decreasePrecision) {
    const rounded = Math.round(price / 100) * 100
    return formatNumber(rounded, options?.omitUnit ? undefined : '€', true)
  }
  return formatNumber(price, options?.omitUnit ? undefined : '€', true)
}

export const formatEer = (eer: number, options?: PrecisionOptions): string => {
  const fractionDigits = options?.increasePrecision ? 1 : 0

  return `${eer.toLocaleString('fi-FI', {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  })}`
}

export const formatLargeEmission = (
  emission: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    emission / 1000,
    options?.omitUnit ? undefined : largeEmissionUnit,
    true,
  )

export const formatLargeEmissionPerYear = (
  emission: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    emission / 1000,
    options?.omitUnit ? undefined : largeEmissionPerYearUnit,
    true,
  )

export const formatEmissionPerArea = (
  emissionPerArea: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    emissionPerArea,
    options?.omitUnit ? undefined : emissionPerAreaUnit,
    true,
  )

export const formatEmissionPerYear = (
  emission: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    emission,
    options?.omitUnit ? undefined : emissionPerYearUnit,
    true,
  )

export const parseLocaleDecimalToFloat = (value: string): number => {
  const parsedValue = parseFloat(value.replace(/,/g, '.'))
  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

export const convertKgsToTons = (value: number): number => value / 1000

export const convertTonsToKgs = (value: number): number => value * 1000

export const formatTotalAirFlowPerArea = (
  airFlow: number,
  options?: CommonFormatOptions,
): string =>
  formatNumber(
    airFlow * 1000,
    options?.omitUnit ? undefined : totalSpecificAirFlowPerAreaUnit,
    true,
  )
